import { DirtyFieldsType, Operation } from '@occc-app/offerHelpers/types';
import { makeDeleteOperation } from '@occc-app/offerHelpers/operations/makeDeleteOperation';
import {
  Limit_Time_Period,
  LimitCreateMutationVariables,
  LimitFragment,
  LimitUpdateMutationVariables,
  OfferFragment,
} from '@occc/graphql';
import { isFieldDirty } from '@occc-app/offerHelpers/utils';
import { limitHelper } from '@occc-app/offerHelpers/utils/limitHelper';

import { LimitsInConstraintGroupFormValue } from '../types';

const getLimitCreateOperation = (
  offerId: string,
  limit: number,
  timePeriod: Limit_Time_Period,
): Operation<'limitCreate', LimitCreateMutationVariables> => {
  return {
    name: 'limitCreate',
    variables: {
      input: {
        offerId,
        configuration: {
          membershipProgressInTime: {
            limit,
            timePeriod,
          },
        },
      },
    },
  };
};

const getLimitUpdateOperation = (
  id: string,
  limit: number,
  timePeriod: Limit_Time_Period,
): Operation<'limitUpdate', LimitUpdateMutationVariables> => {
  return {
    name: 'limitUpdate',
    variables: {
      input: {
        id,
        configuration: {
          membershipProgressInTime: {
            limit,
            timePeriod,
          },
        },
      },
    },
  };
};

export const getProgressPerPeriodLimitOperations = (
  offerId: string,
  values: Pick<LimitsInConstraintGroupFormValue, 'isEnabled' | 'progressPerPeriod'>,
  dirtyFields: DirtyFieldsType,
): Operation[] => {
  if ((!values.isEnabled || !values.progressPerPeriod?.isEnabled) && values.progressPerPeriod?.id) {
    return [makeDeleteOperation('limitDelete', values.progressPerPeriod.id)];
  }

  if (
    !values.progressPerPeriod?.id &&
    values.progressPerPeriod?.isEnabled &&
    typeof values.progressPerPeriod.limit === 'number'
  ) {
    return [
      getLimitCreateOperation(
        offerId,
        values.progressPerPeriod.limit,
        values.progressPerPeriod.timePeriod,
      ),
    ];
  }

  const isLimitInputDirty = isFieldDirty<{
    awardCount: LimitsInConstraintGroupFormValue;
  }>(dirtyFields, 'awardCount.progressPerPeriod.limit');
  const isTimePeriodDirty = isFieldDirty<{
    awardCount: LimitsInConstraintGroupFormValue;
  }>(dirtyFields, 'awardCount.progressPerPeriod.timePeriod');

  if (
    values.progressPerPeriod?.id &&
    values.progressPerPeriod?.isEnabled &&
    typeof values.progressPerPeriod.limit === 'number' &&
    (isLimitInputDirty || isTimePeriodDirty)
  ) {
    return [
      getLimitUpdateOperation(
        values.progressPerPeriod.id,
        values.progressPerPeriod.limit,
        values.progressPerPeriod.timePeriod,
      ),
    ];
  }

  return [];
};

export const hasProgressPerPeriodLimit = (limits: LimitFragment[]) => {
  return limits.some(limitHelper.isMembershipProgressInTime);
};

export const getProgressPerPeriodLimitInitialValues = (
  offer: OfferFragment,
): LimitsInConstraintGroupFormValue['progressPerPeriod'] => {
  const limit = offer.limits.find(limitHelper.isMembershipProgressInTime);

  if (limit) {
    return {
      isEnabled: true,
      id: limit.id,
      limit: limit.limit,
      timePeriod: limit.timePeriod,
    };
  }

  return {
    isEnabled: false,
    timePeriod: Limit_Time_Period.Day,
  };
};
